"use client"

import * as React from "react"
import { useState } from "react"
import Link from "next/link"
import { CreateFolderModal } from "@/components/modal-dialogs/create-folder-modal/create-folder-modal"
import { PartnerWithUsModal } from "@/components/modal-dialogs/partner-with-us-modal/partner-with-us-modal"
import { SettingsModal } from "@/components/modal-dialogs/settings-modal/settings-modal"
import { SocialsDropup, WhatsAppIcon } from "@/components/dashboard/socials-dropup"
import { NewDeckMenu } from "@/components/dashboard/new-deck-menu"
import { usePathname, useRouter } from "next/navigation"
import dynamic from "next/dynamic"
import { cn } from "@/lib/utils"
import {
    LayoutGrid,
    BarChart3,
    Package,
    FolderOpen,
    Plus,
    Settings,
    PanelLeftClose,
    PanelLeftOpen,
    GalleryVerticalEnd,
    ArrowRight,
    Handshake,
    Play,
    Loader2,
    Globe,
    Database,
    LogIn,
} from "lucide-react"
import { useAppSelector } from "@/app/store/hooks"
import { useGetAllFoldersQuery } from "@/app/store/api/pitchDeckApi"
import { trackEvent } from "@/lib/mixpanel"
import Tooltip from "@mui/material/Tooltip"

export const mainNav = [
    {
        title: "Explore",
        href: "/",
        icon: LayoutGrid,
    },
    {
        title: "My Decks",
        href: "/decks",
        icon: GalleryVerticalEnd,
    },
    {
        title: "Analytics",
        href: "/analytics",
        icon: BarChart3,
    },
    // {
    //     title: "Themes",
    //     href: "/themes",
    //     icon: Palette,
    // },
    {
        title: "Knowledge Base",
        href: "/knowledge-base",
        icon: Database,
    },
]

import { Avatar } from "@/components/ui/avatar"
import { getInitials } from "@/lib/utils"
import IconButton from "@mui/material/IconButton"
import { Skeleton } from "@mui/material"

interface DashboardSidebarProps {
    defaultCollapsed?: boolean
}

const LoginPopover = dynamic(
    () => import("../auth/login-popover").then((mod) => mod.LoginPopover),
    { ssr: false }
)

export function DashboardSidebar({ defaultCollapsed = false }: DashboardSidebarProps) {
    const pathname = usePathname()
    const router = useRouter()
    const { user, token, isAuthenticated } = useAppSelector((state) => state.auth)
    const isLoggedIn = !!(token && isAuthenticated)
    const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed)
    const [isLoginPopoverOpen, setIsLoginPopoverOpen] = useState(false)

    const requireAuth = (e: React.MouseEvent) => {
        if (!isLoggedIn) {
            e.preventDefault()
            setIsLoginPopoverOpen(true)
            return true
        }
        return false
    }
    const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false)
    const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false)
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)

    // Fetch folders from API
    const { data: foldersData, isLoading: foldersLoading } = useGetAllFoldersQuery(undefined, { skip: !isLoggedIn })
    // Filter out placeholder folders
    const PLACEHOLDER_FOLDERS = ["personal", "work", "archive"]
    const folders = (foldersData?.data || []).filter(
        folder => !PLACEHOLDER_FOLDERS.includes(folder.name.toLowerCase())
    )

    // Scroll state for gradient (Bottom only)
    const [showBottomGradient, setShowBottomGradient] = useState(true)
    const folderContainerRef = React.useRef<HTMLDivElement>(null)

    const handleScroll = () => {
        if (folderContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = folderContainerRef.current
            // Show bottom gradient if not at the verrry bottom
            setShowBottomGradient(scrollHeight - scrollTop - clientHeight > 5)
        }
    }

    // Effect to check bottom gradient on mount/resize
    React.useEffect(() => {
        const checkScroll = () => {
            // We need to wait for layout/render? 
            // Just trigger handleScroll
            handleScroll();
        }

        // Initial check
        checkScroll();

        window.addEventListener('resize', checkScroll)
        return () => window.removeEventListener('resize', checkScroll)
    }, [])

    // Helper for initials
    const initials = getInitials(user?.firstName, user?.lastName);

    return (
        <div
            className={cn(
                "flex flex-col h-full transition-all duration-300 ease-in-out overflow-hidden",
                isCollapsed ? "w-14" : "w-52"
            )}
        >
            {/* Header */}
            <div className={cn("flex h-14 items-center flex-shrink-0", isCollapsed ? "justify-center px-0" : "px-5")}>
                {!isCollapsed ? (
                    <div className="flex flex-col w-full">
                        <div className="flex items-center gap-2 font-bold text-lg leading-none">
                            <Link href="/">Deckwale</Link>
                            <button
                                onClick={() => setIsCollapsed(true)}
                                aria-label="Collapse sidebar"
                                className="ml-auto cursor-pointer text-[#A3B3D1] hover:text-foreground transition-colors"
                            >
                                <PanelLeftClose className="h-4 w-4" />
                            </button>
                        </div>
                        <span className="text-[10px] font-semibold text-blue-600 leading-none mt-1">by Seedicon</span>
                    </div>
                ) : (
                    <button
                        onClick={() => setIsCollapsed(false)}
                        aria-label="Expand sidebar"
                        className="text-slate-500 cursor-pointer hover:text-foreground transition-colors p-2"
                    >
                        <PanelLeftOpen className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Middle Content - Flex Container */}
            <div className="flex-1 flex flex-col overflow-hidden pt-1 gap-1.5">

                {/* New Deck Menu (Fixed Height) */}
                <div className={cn("flex-shrink-0 rounded-2xl", isCollapsed ? "px-0 flex justify-center" : "px-4")}>
                    <NewDeckMenu isCollapsed={isCollapsed} />
                </div>


                {/* Main Nav (Fixed Height) */}
                <nav className={cn("grid gap-1 flex-shrink-0", isCollapsed ? "px-2" : "px-4")}>
                    {mainNav.map((item, index) => {
                        const isActive = pathname === item.href
                        const Icon = item.icon
                        const needsAuth = item.href !== "/" && item.href !== "/discover"

                        if (isCollapsed) {
                            return (
                                <Tooltip key={index} title={item.title} placement="right" arrow>
                                    <Link
                                        href={item.href}
                                        onClick={(e) => needsAuth && requireAuth(e)}
                                        className={cn(
                                            "flex h-9 w-9 items-center justify-center rounded-lg hover:text-accent-foreground transition-colors mx-auto relative",
                                            isActive ? "bg-[#e6e6e6] text-indigo-600" : "text-slate-600 hover:text-slate-800"
                                        )}
                                    >
                                        <Icon className={cn("h-4 w-4", isActive ? "text-slate-900" : "text-slate-600 group-hover:text-slate-900")} strokeWidth={1.5} />
                                    </Link>
                                </Tooltip>
                            )
                        }

                        return (
                            <Link
                                key={index}
                                href={item.href}
                                onClick={(e) => needsAuth && requireAuth(e)}
                                className={cn(
                                    "relative group flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap transition-all duration-200 overflow-hidden",
                                    isActive
                                        ? "bg-[#e6e6e6] text-slate-900"
                                        : "text-slate-700 hover:text-slate-900"
                                )}
                            >
                                {isActive && (
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[60%] w-1 rounded-r-full bg-gradient-to-b from-blue-400 to-indigo-600" />
                                )}
                                <Icon className={cn("h-3.5 w-3.5 transition-colors flex-shrink-0", isActive ? "text-slate-900" : "text-slate-400 group-hover:text-slate-900")} />
                                <span className="truncate">{item.title}</span>
                            </Link>
                        )
                    })}
                </nav>

                {/* Divider - Fixed */}
                {!isCollapsed && (
                    <div className="mx-7 h-px bg-[#e4edff] flex-shrink-0" />
                )}

                {/* Folders Section - Flexible Height / Independent Scroll */}
                {!isCollapsed && (
                    <div className="flex-1 flex flex-col min-h-0 relative">
                        {/* Folders Header - Fixed */}
                        <div className="flex items-center justify-between px-[26px] text-[11px] font-semibold uppercase text-slate-600 mb-1.5 flex-shrink-0 whitespace-nowrap overflow-hidden">
                            <span className="truncate">Folders</span>
                            <Tooltip title="Add Folder" placement="right" arrow>
                                <IconButton
                                    size="small"
                                    className="!text-slate-600 hover:!text-black transition-colors flex-shrink-0"
                                    onClick={(e: React.MouseEvent) => requireAuth(e) || setIsCreateFolderModalOpen(true)}
                                >
                                    <Plus className="h-3.5 w-3.5 cursor-pointer" />
                                </IconButton>
                            </Tooltip>
                        </div>

                        {/* Bottom Scroll Gradient Overlay - Absolute Bottom */}
                        <div
                            className={cn(
                                "absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#f4f4f4] via-[#f4f4f4]/80 to-transparent z-10 pointer-events-none transition-opacity duration-300",
                                showBottomGradient ? "opacity-100" : "opacity-0"
                            )}
                        />

                        {/* Scrollable Folder List */}
                        <div
                            className="flex-1 overflow-y-auto px-4 pb-2"
                            ref={folderContainerRef}
                            onScroll={handleScroll}
                        >
                            <nav className="grid gap-1">
                                {foldersLoading ? (
                                    <div className="flex items-center flex-col justify-center py-2 w-full">
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <Skeleton key={index} className="h-2 w-full rounded-xl animate-spin text-slate-400" />
                                        ))}
                                    </div>
                                ) : (
                                    folders.map((folder) => {
                                        const isActive = pathname === `/folders/${folder._id}`

                                        return (
                                            <Link
                                                key={folder._id}
                                                href={`/folders/${folder._id}`}
                                                onClick={(e) => requireAuth(e)}
                                                className={cn(
                                                    "relative group flex items-center gap-3 rounded-lg px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap transition-all duration-200",
                                                    isActive
                                                        ? "bg-[#e6e6e6] text-slate-900"
                                                        : "text-slate-500 hover:text-slate-900"
                                                )}
                                            >
                                                {isActive && (
                                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[60%] w-1 rounded-r-full bg-gradient-to-b from-blue-400 to-indigo-600" />
                                                )}
                                                {folder.logo ? (
                                                    <img src={folder.logo} alt="" className="h-3.5 w-3.5 rounded object-cover flex-shrink-0" />
                                                ) : (
                                                    <FolderOpen className={cn("h-3.5 w-3.5 transition-colors flex-shrink-0", isActive ? "text-slate-900" : "text-slate-400 group-hover:text-slate-900")} />
                                                )}
                                                <span className="truncate">{folder.name}</span>
                                            </Link>
                                        )
                                    })
                                )}
                            </nav>
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom Section - Socials & Partner Button */}
            <div className={cn("px-4 border-t border-gray-100 flex-shrink-0 flex flex-col", isCollapsed ? "gap-1 items-center" : "gap-0 pt-2")}>

                {/* Landing Page Button */}
                {isCollapsed ? (
                    <Tooltip title="Discover" placement="right" arrow>
                        <Link
                            href="/discover"
                            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white hover:text-slate-900 text-slate-600 transition-colors"
                        >
                            <Globe className="h-4 w-4" />
                        </Link>
                    </Tooltip>
                ) : (
                    <Link
                        href="/discover"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-start gap-3 px-2.5 py-1.5 text-[13px] font-medium text-slate-700 hover:text-slate-900 transition-colors cursor-pointer whitespace-nowrap overflow-hidden"
                    >
                        <Globe className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-900 transition-colors flex-shrink-0" />
                        <span className="truncate">Discover</span>
                    </Link>
                )}

                {/* Watch Demo Button */}
                {isCollapsed ? (
                    <Tooltip title="Watch Demo" placement="right" arrow>
                        <a
                            href="https://www.youtube.com/@DeckwaleAI"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white hover:text-slate-900 text-slate-600 transition-colors"
                        >
                            <Play className="h-4 w-4" />
                        </a>
                    </Tooltip>
                ) : (
                    <a
                        href="https://www.youtube.com/@DeckwaleAI"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-start gap-3 px-2.5 py-1.5 text-[13px] font-medium text-slate-700 hover:text-slate-900 transition-colors cursor-pointer whitespace-nowrap overflow-hidden"
                    >
                        <Play className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-900 transition-colors flex-shrink-0" />
                        <span className="truncate">Watch Demo</span>
                    </a>
                )}

                {/* WhatsApp Community Button */}
                {isCollapsed ? (
                    <Tooltip title="Join Community" placement="right" arrow>
                        <a
                            href="https://chat.whatsapp.com/B9gMJ4FvMNHGIgKQMux1zq"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white hover:text-slate-900 text-slate-600 transition-colors"
                        >
                            <WhatsAppIcon className="h-4 w-4" />
                        </a>
                    </Tooltip>
                ) : (
                    <a
                        href="https://chat.whatsapp.com/B9gMJ4FvMNHGIgKQMux1zq"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center justify-start gap-3 px-2.5 py-1.5 text-[13px] font-medium text-slate-700 hover:text-slate-900 transition-colors cursor-pointer whitespace-nowrap overflow-hidden"
                    >
                        <WhatsAppIcon className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-900 transition-colors flex-shrink-0" />
                        <span className="truncate">Join Community</span>
                    </a>
                )}

                <SocialsDropup isCollapsed={isCollapsed} />

                {/* Partner Button
                {isCollapsed ? (
                    <Tooltip title="Partner with us" placement="right" arrow>
                        <button
                            onClick={() => setIsPartnerModalOpen(true)}
                            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-blue-50 hover:text-blue-600 text-slate-600 transition-colors"
                        >
                            <Handshake className="h-4 w-4" />
                        </button>
                    </Tooltip>
                ) : (
                    <div className="">
                        <button
                            onClick={() => setIsPartnerModalOpen(true)}
                            className="relative cursor-pointer w-full group overflow-hidden rounded-xl bg-white border border-slate-200 px-2.5 py-1.5 text-left shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all duration-300 hover:shadow-[0_8px_16px_rgba(0,0,0,0.06)] hover:-translate-y-0.5 hover:border-blue-200 ring-offset-2 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-blue-50/30 to-blue-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                            <div className="relative flex items-center justify-between">
                                <div className="flex flex-col gap-0">
                                    <span className="text-[13px] font-bold text-slate-800 tracking-tight">Partner with us</span>
                                    <div className="flex items-center gap-1 group/text">
                                        <span className="text-[11px] font-medium text-slate-500 group-hover:text-blue-600 transition-colors">Grow together</span>
                                        <ArrowRight className="w-2.5 h-2.5 text-slate-400 -ml-0.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-blue-600 transition-all duration-300" />
                                    </div>
                                </div>
                                <div className="h-7 w-7 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center group-hover:bg-blue-600 group-hover:border-blue-600 transition-all duration-300 shadow-sm group-hover:shadow-blue-200">
                                    <Handshake className="w-3.5 h-3.5 text-slate-400 group-hover:text-white transition-colors" />
                                </div>
                            </div>
                        </button>
                    </div>
                )}
                */}
            </div>

            <div className="px-4 pb-4 flex mt-1 flex-col gap-2 flex-shrink-0">
                {isCollapsed && !isLoggedIn ? (
                    <div className="flex justify-center">
                        <Tooltip title={isLoggedIn ? "Settings" : "Login"} placement="right" arrow>
                            <button
                                onClick={(e) => requireAuth(e) || setIsLoginPopoverOpen(true)}
                                className="flex cursor-pointer h-9 w-9 items-center justify-center rounded-lg hover:bg-accent hover:text-accent-foreground text-slate-600 transition-colors"
                            >
                                <LogIn className="h-4 w-4" />
                            </button>
                        </Tooltip>
                    </div>
                ) : isLoggedIn && !isCollapsed ? (
                    <div
                        onClick={() => { trackEvent("Settings Opened", { source: "sidebar" }); setIsSettingsModalOpen(true); }}
                        className="flex items-center cursor-pointer gap-1.5 px-1.5 py-1.5 rounded-md bg-[#e6e6e6] hover:bg-[#d8d8d8]">
                        <Avatar
                            className="h-6 w-6 flex-shrink-0"
                            src={user?.imgUrl}
                            fallback={initials}
                            fallbackClassName="bg-orange-500 text-white font-bold text-[10px]"
                        />

                        <div className="flex-1 relative min-w-0 flex items-center">
                            <span className="truncate text-[13px] font-medium text-slate-700 peer mr-2 transition-opacity duration-200 peer-hover:opacity-0">
                                {user?.firstName || "User"}
                            </span>

                            <button onClick={() => { trackEvent("Settings Opened", { source: "sidebar" }); setIsSettingsModalOpen(true); }} className="flex-shrink-0 ml-auto cursor-pointer">
                                <Settings className="h-3.5 w-3.5 text-[#A3B3D1] hover:text-slate-600" />
                            </button>

                            <div className="absolute inset-0 bg-transparent flex items-center opacity-0 peer-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                                <span className="text-[13px] font-medium text-slate-700 truncate">
                                    {user?.firstName} {user?.lastName}
                                </span>
                            </div>
                        </div>
                    </div>
                ) : isLoggedIn && isCollapsed ? (
                     <Avatar 
                            onClick={() => { trackEvent("Settings Opened", { source: "sidebar" }); setIsSettingsModalOpen(true); }}
                            className="h-6 w-6 mb-1 flex-shrink-0 cursor-pointer"
                            src={user?.imgUrl}
                            fallback={initials}
                            fallbackClassName="bg-orange-500 text-white font-bold text-[10px]"
                        />
                ) : (
                    <div onClick={() => setIsLoginPopoverOpen(true)} className="flex items-center justify-center font-medium cursor-pointer text-[12px] gap-1.5 px-1 py-1.5 bg-blue-600 text-primary-foreground hover:bg-blue-700 transition-all rounded-md">
                        Sign In
                        <LogIn className="h-4 w-4" />
                    </div>
                )}
            </div>
            <LoginPopover
                isOpen={isLoginPopoverOpen}
                onClose={() => setIsLoginPopoverOpen(false)}
            />
            <CreateFolderModal
                isOpen={isCreateFolderModalOpen}
                onClose={() => setIsCreateFolderModalOpen(false)}
            />
            <PartnerWithUsModal
                isOpen={isPartnerModalOpen}
                onClose={() => setIsPartnerModalOpen(false)}
            />
            <SettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
            />
        </div>
    )
}
